import {
  Box, Flex, Text, Spinner,
  Input, InputGroup, InputLeftElement,
  Avatar,
  Image, Link, Tag, TagLabel,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { Search2Icon, AttachmentIcon } from "@chakra-ui/icons";
import CustomToast from "../CustomToast";
import { useFetch, baseUrl } from "../../hooks/useFetch";
import { showDate, showDateTime, showTime, showTimeForHistory, stripHtml } from "../../utilities/helper";
import LoginIcon from "../../assets/Images/History/Login-icon.svg"
import LogoutIcon from "../../assets/Images/History/Logout-icon.svg"
import UpdateIcon from "../../assets/Images/History/Update-icon.svg"
import DeleteIcon from "../../assets/Images/History/Delete-icon.svg"
import ApproveIcon from "../../assets/Images/History/Approve-icon.svg"
import RejectIcon from "../../assets/Images/History/Reject-icon.svg"
import AssignIcon from "../../assets/Images/History/Assign-icon.svg"
import UploadIcon from "../../assets/Images/History/Upload-icon.svg"
import DownloadIcon from "../../assets/Images/History/Download-icon.svg"
import EmailSentIcon from "../../assets/Images/History/EmailSent-icon.svg"
import SelfAssignIcon from "../../assets/Images/History/SelfAssign-icon.svg"
import SentIcon from "../../assets/Images/History/Sent-icon.svg"
import MeetingCreatedIcon from "../../assets/Images/History/MeetingCreated-icon.svg"
import MeetingUpdateIcon from "../../assets/Images/History/MeetingUpdate-icon.svg"
import FollowUpCreatedIcon from "../../assets/Images/History/FollowUp-Created-icon.svg"
import FollowUpUpdateIcon from "../../assets/Images/History/FollowUp-Date-icon.svg"
import DefaultIcon from "../../assets/Images/History/Default-icon.svg"
import DuplicateIcon from "../../assets/Images/History/Dupe-icon.svg"
import CreateIcon from "../../assets/Images/History/Create-icon.svg"
import UserIcon from "../../assets/Images/History/User-icon.svg"
import AutoAssignIcon from "../../assets/Images/History/Auto-Assign-icon.svg"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IActivityChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  originalFileName?: string | null;
  filePath?: string | null;
}

export interface IActivityAttachment {
  id?: number;
  fileName?: string;
  relativePath?: string;
  filePath?: string;
  originalName?: string;
}

export interface IActivityLog {
  id: number;
  action: string;
  message: string;
  module: string;
  performedBy: string;
  icon?: string;
  color?: string;
  createdAt?: string;
  oldValue?: string;
  newValue?: string;
  changes?: IActivityChange[];
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  attachments?: IActivityAttachment[];
}

export interface UniversalActivityHistoryProps {
  module: string;
  recordId: number | string | null | undefined;
  // tabs?: Array<{
  //   label: string;
  //   value: string;
  //   filter: (log: IActivityLog) => boolean;
  // }>;
  maxH?: string;
  refreshKey?: number;
}

const ACTION_ICONS: Record<string, string> = {
  CREATE: CreateIcon,
  UPDATE: UpdateIcon,
  DELETE: DeleteIcon,

  GENERATE: CreateIcon,

  LOGIN: LoginIcon,
  LOGOUT: LogoutIcon,

  APPROVE: ApproveIcon,
  REJECT: RejectIcon,

  ASSIGN: AssignIcon,
  REASSIGNED: AssignIcon,
  SELF_ASSIGN: SelfAssignIcon,
  Auto_Assign: AutoAssignIcon,

  UPLOAD: UploadIcon,
  DOWNLOAD: DownloadIcon,

  EMAIL_SENT: EmailSentIcon,
  SENT: SentIcon,

  Duplicate: DuplicateIcon,

  STATUS_CHANGE: UpdateIcon,

  Qualified: ApproveIcon,

  MEETING_CREATED: MeetingCreatedIcon,
  MEETING_UPDATE: MeetingUpdateIcon,

  FOLLOW_UP_CREATED: FollowUpCreatedIcon,
  FOLLOW_UP_UPDATE: FollowUpUpdateIcon,
};


// ─── Constants ────────────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  CREATE: { bg: "#ECFDF3", text: "#027A48", dot: "#12B76A" },
  UPDATE: { bg: "#EFF8FF", text: "#175CD3", dot: "#2E90FA" },
  DELETE: { bg: "#FEF3F2", text: "#B42318", dot: "#F04438" },

  LOGIN: { bg: "#FFF6ED", text: "#C4320A", dot: "#F79009" },
  LOGOUT: { bg: "#FFF6ED", text: "#C4320A", dot: "#F79009" },

  APPROVE: { bg: "#ECFDF3", text: "#027A48", dot: "#12B76A" },
  REJECT: { bg: "#FEF3F2", text: "#B42318", dot: "#F04438" },

  ASSIGN: { bg: "#F4F3FF", text: "#5925DC", dot: "#7A5AF8" },
  REASSIGNED: { bg: "#F2EFFF", text: "#7A5AF8", dot: "#EE46BC" },
  SELF_ASSIGN: { bg: "#F4F3FF", text: "#5925DC", dot: "#7A5AF8" },
  Auto_Assign: { bg: "#F4F3FF", text: "#5925DC", dot: "#7A5AF8" },

  UPLOAD: { bg: "#ECFDFF", text: "#0E7090", dot: "#06AED4" },
  DOWNLOAD: { bg: "#EEF4FF", text: "#3538CD", dot: "#6172F3" },

  EMAIL_SENT: { bg: "#ECFDFF", text: "#0E7090", dot: "#06AED4" },
  SENT: { bg: "#ECFDFF", text: "#0E7090", dot: "#06AED4" },

  Duplicate: { bg: "#FDF2FA", text: "#C11574", dot: "#EE46BC" },

  STATUS_CHANGE: { bg: "#EFF8FF", text: "#175CD3", dot: "#2E90FA" },

  Qualified: { bg: "#ECFDF3", text: "#027A48", dot: "#12B76A" },

  MEETING_CREATED: { bg: "#EEF4FF", text: "#3538CD", dot: "#6172F3" },
  MEETING_UPDATE: { bg: "#FEEDEC", text: "#F04438", dot: "#12B76A" },

  FOLLOW_UP_CREATED: { bg: "#FFF6ED", text: "#C4320A", dot: "#F79009" },
  FOLLOW_UP_UPDATE: { bg: "#FEF3F2", text: "#B42318", dot: "#F04438" },

  GENERATE: { bg: "#F4F3FF", text: "#5925DC", dot: "#7A5AF8" },

  Default: { bg: "#EEF4FF", text: "#3538CD", dot: "#6172F3" },
};

const AVATAR_COLORS = [
  "#6172F3", "#EE46BC", "#15B79E", "#F79009",
  "#F04438", "#12B76A", "#7A5AF8", "#2E90FA",
];


// ─── Helpers ──────────────────────────────────────────────────────────────────

const getColors = (action: string) =>
  ACTION_COLORS[action] ?? ACTION_COLORS["Default"];

const getInitials = (name: string) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

const avatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatDate = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    })
    : "Unknown Date";

const formatTime = (d?: string) =>
  d
    ? new Date(d).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    : "";

// ─── ChangeRow ────────────────────────────────────────────────────────────────

const ChangeRow = ({
  field,
  oldValue,
  newValue,
  filePath,
  originalFileName,
  colors,
}: {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  filePath?: string | null;
  originalFileName?: string | null;
  colors: { bg: string; text: string; dot: string };
}) => {
  const hasOld = oldValue !== null && oldValue !== "";
  const hasNew = newValue !== null && newValue !== "";

  if (!hasOld && !hasNew) {
    return null;
  }

  const downloadAuditAttachment = async (targetPath?: string | null, targetName?: string | null) => {
    const pathToUse = targetPath || filePath || newValue || "";
    const nameToUse = targetName || originalFileName || newValue || "attachment";
    if (!pathToUse) return;

    try {
      const token = localStorage.getItem("token") || "";
      const response = await fetch(
        `${baseUrl}Lead/download-file?filePath=${encodeURIComponent(pathToUse)}&fileName=${encodeURIComponent(nameToUse)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nameToUse;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed", e);
    }
  };

  const formatFieldValue = (value: string | null) => {
    if (!value) return value;

    const isDateTime = /^\d{4}-\d{2}-\d{2}T/.test(value);
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
    const isTimeOnly = /^\d{2}:\d{2}:\d{2}$/.test(value);

    if (field === "EmailBody") {
      return stripHtml(value);
    }
    if (isDateTime) {
      return showDateTime(value);
    }

    if (isDateOnly) {
      return showDate(value);
    }
    if (isTimeOnly) {
      return showTimeForHistory(value);
    }

    return value;
  };

  const isAttachmentField = field.toLowerCase().includes("attachment") || field.toLowerCase().includes("file");

  const renderValue = (val: string | null, isOld = false) => {
    if (!val) return null;
    if (isAttachmentField) {
      const files = val.split(",").map(s => s.trim()).filter(Boolean);
      return (
        <Flex wrap="wrap" gap={1}>
          {files.map((fileName, idx) => {
            const displayName = fileName.split("/").pop() || fileName;

            return (
              <Tag
                key={idx}
                size="sm"
                colorScheme={isOld ? "red" : "blue"}
                variant="subtle"
                borderRadius="md"
                cursor="pointer"
                onClick={() => downloadAuditAttachment(filePath || fileName, displayName)}
                _hover={{ opacity: 0.8 }}
              >
                <AttachmentIcon mr={1} boxSize="10px" />
                <TagLabel fontSize="11px" textDecoration="underline">{displayName}</TagLabel>
              </Tag>
            );
          })}
        </Flex>
      );
    }

    return (
      <Text
        fontSize="12px"
        color={isOld ? "#991B1B" : "#374151"}
        bg={isOld ? "#FEF2F2" : "#F3F4F6"}
        px={2}
        py="2px"
        borderRadius="4px"
        fontWeight="400"
        opacity={isOld ? 0.75 : 1}
      >
        {formatFieldValue(val)}
      </Text>
    );
  };

  return (
    <Flex align="center" gap={2} flexWrap="wrap" py="4px" _last={{ borderBottom: "none", pb: 0 }}>
      <Text fontSize="12px" color="#535458" fontWeight="600" minW="110px">
        <Text className="font-poppins text_sm text_regular" color="#535458" minW="110px">
          {field.replace(/_/g, " ")}
        </Text>
      </Text>

      {hasOld && hasNew && (
        <>
          {renderValue(oldValue, true)}
          <Text fontSize="12px" color="#6B7280">→</Text>
          {renderValue(newValue, false)}
        </>
      )}

      {!hasOld && hasNew && renderValue(newValue, false)}
      {hasOld && !hasNew && renderValue(oldValue, true)}
    </Flex>
  );
};

// ─── LogCard ──────────────────────────────────────────────────────────────────

const LogCard = ({ log }: { log: IActivityLog }) => {
  const colors = getColors(log.action);
  const hasChanges = log.changes && log.changes.length > 0;
  const hasLegacy = log.oldValue || log.newValue;
  const hasAttachments = log.attachments && log.attachments.length > 0;

  return (
    <Box border="0.5px solid #E5E7EB" borderRadius="8px" px={3} py={2}>
      <Flex align="center" justify="space-between" mb={1}>
        <Flex align="center">
          <Image src={UserIcon} boxSize="15px" mt="2px" alt="User" />
          <Text fontSize="13px" fontWeight="500" color="#9F9F9F" className="font-poppins text_sm text_regular" ml={2}>
            Changed By <Text as="span" mr={2} className="font-poppins text_sm text_semibold" color={colors.text}>  {log.performedBy}</Text>
          </Text>
        </Flex>
      </Flex>
      {hasChanges ? (
        log.changes!.map((c, i) => (
          <ChangeRow
            key={i}
            field={c.field}
            oldValue={c.oldValue}
            newValue={c.newValue}
            filePath={c.filePath}
            originalFileName={c.originalFileName}
            colors={colors}
          />
        ))
      ) : hasLegacy ? (
        <Flex align="center" gap={2} flexWrap="wrap">

          <Text fontSize="12px" color="#374151" bg="#F3F4F6" px={2} py="2px" borderRadius="4px" fontWeight="400">
            {log.oldValue}
          </Text>
          <Text fontSize="12px" color="#6B7280">→</Text>
          <Text fontSize="12px" color={colors.text} bg={colors.bg} px={2} py="2px" borderRadius="4px" fontWeight="400">
            {log.newValue}
          </Text>
        </Flex>
      ) : (
        <Text fontSize="12px" color="#374151" lineHeight="1.6">
            {stripHtml(log.message)}
        </Text>
      )}

      {hasAttachments && (
        <Box mt={2} pt={2} borderTop="1px dashed #E5E7EB">
          <Text fontSize="11px" fontWeight="600" color="#6B7280" mb={1}>
            Attached Files:
          </Text>
          <Flex wrap="wrap" gap={1.5}>
            {log.attachments!.map((att, idx) => {
              const name = att.fileName || att.originalName || "Attachment";
              const path = att.relativePath || att.filePath;
              const fullUrl = path ? (path.startsWith("http") ? path : `${baseUrl}${path}`) : null;
              return (
                <Tag key={idx} size="sm" colorScheme="blue" variant="subtle" borderRadius="md">
                  <AttachmentIcon mr={1} boxSize="10px" />
                  {fullUrl ? (
                    <Link href={fullUrl} isExternal color="blue.600" textDecoration="underline">
                      <TagLabel fontSize="11px">{name}</TagLabel>
                    </Link>
                  ) : (
                    <TagLabel fontSize="11px">{name}</TagLabel>
                  )}
                </Tag>
              );
            })}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const UniversalActivityHistory = ({
  module,
  recordId,
  maxH = "100%",  refreshKey = 0,
}: UniversalActivityHistoryProps) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [logs, setLogs] = useState<IActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [historyKey, setHistoryKey] = useState(0);

  // const tabs = tabsProp ?? DEFAULT_TABS;
  // const [activeTab, setActiveTab] = useState(tabs[0]?.value ?? "all");

  // ── Fetch ──────────────────────────────────────────────────────────────────
 const loadHistory = async () => {
    if (!recordId || !module) return;

    setLoading(true);

    const params = new URLSearchParams({
      recordId: String(recordId),
      moduleName: module,
    }).toString();

    const res = await fetchApi("auditlog", "GET", null, params);

    if (res) {
      setLogs(Array.isArray(res) ? res : []);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadHistory();
  }, [recordId, module]);

useEffect(() => {
    const handleRefresh = (e: Event) => {
        const event = e as CustomEvent;

        if (
            event.detail?.module === module &&
            String(event.detail?.recordId) === String(recordId)
        ) {
            loadHistory();
        }
    };

    window.addEventListener("history-refresh", handleRefresh);

    return () =>
        window.removeEventListener("history-refresh", handleRefresh);
}, [module, recordId]);



  // Reset tab when module changes
  // useEffect(() => {
  //   setActiveTab(tabs[0]?.value ?? "all");
  // }, [module]);

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = logs.filter((l) => {
    const q = search.toLowerCase();

    return (
      !search ||
      l.action.toLowerCase().includes(q) ||
      l.message.toLowerCase().includes(q) ||
      l.performedBy.toLowerCase().includes(q) ||
      l.changes?.some(
        (c) =>
          c.field.toLowerCase().includes(q) ||
          c.oldValue?.toLowerCase().includes(q) ||
          c.newValue?.toLowerCase().includes(q)
      )
    );
  });


  const grouped: Record<string, IActivityLog[]> = {};
  filtered.forEach((log) => {
    const key = showDateTime(log.createdAt);
    (grouped[key] ??= []).push(log);
  });


  return (
    <Box maxH={maxH} overflowY="auto">

      {/* Search */}
      {/* <InputGroup mb={3} size="sm">
        <InputLeftElement pointerEvents="none">
          <Search2Icon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={`Search ${module} activity...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          borderRadius="8px"
          bg="#F9FAFB"
          fontSize="13px"
        />
      </InputGroup> */}

      {/* Tabs — pill style */}
      {/* {tabs.length > 1 && (
        <Flex gap={2} mb={4} flexWrap="wrap">
          {tabs.map((tab) => (
            <Box
              key={tab.value}
              as="button"
              onClick={() => setActiveTab(tab.value)}
              px={3}
              py="5px"
              borderRadius="999px"
              border="0.5px solid"
              borderColor={activeTab === tab.value ? "#0F766E" : "#E5E7EB"}
              bg={activeTab === tab.value ? "#E7F8F7" : "#F9FAFB"}
              color={activeTab === tab.value ? "#0F766E" : "#6B7280"}
              fontSize="12px"
              fontWeight="400"
              cursor="pointer"
              transition="all 0.15s"
              _hover={{
                bg: activeTab === tab.value ? "#E7F8F7" : "#F3F4F6",
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Flex>
      )} */}

      {/* Body */}
      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner color="blue.500" size="sm" />
        </Flex>
      ) : filtered.length === 0 ? (
        <Flex justify="center" align="center" py={10} direction="column" gap={2}>
          <Text fontSize="13px" color="gray.400">
            No activity found
          </Text>
        </Flex>
      ) : (
        Object.entries(grouped).map(([date, items]) => (
          <Box key={date} mb={6}>

            {/* Date separator */}
            {/* <Flex align="center" gap={3} mb={3}>
              <Box flex="1" h="0.5px" bg="#E5E7EB" />
              <Text fontSize="10px" fontWeight="400" color="#9CA3AF" textTransform="uppercase" letterSpacing="0.05em" whiteSpace="nowrap">
                {date}
              </Text>
              <Box flex="1" h="0.5px" bg="#E5E7EB" />
            </Flex> */}

            {/* Timeline */}
            <Box position="relative">
              <Box position="absolute" left="10px" top="4px" bottom="4px" w="0.5px" bg="#E5E7EB" zIndex={0} />

              {items.map((log) => {
                const colors = getColors(log.action);
                const color = avatarColor(log.performedBy);
                const iconSrc = ACTION_ICONS[log.action] || DefaultIcon;

                return (
                  <Flex key={log.id} gap={3} mb={4} position="relative" zIndex={1}>
                    {/* <Avatar name={log.performedBy} bg={color} color="white" size="sm" fontSize="11px" fontWeight="500" border="2px solid white" mt="1px" /> */}
                    <Image src={iconSrc} boxSize="24px" minW="24px" mt="2px" alt={log.action} />
                    <Box flex="1" minW={0}>
                      <Flex align="center" gap={2} mb="6px" flexWrap="wrap">

                        <Flex align="center" gap="4px" px="8px" py="2px" borderRadius="5px" bg={colors.bg} display="inline-flex">
                          <Text className="font-poppins text_medium text_xsm" color={colors.text}>
                            {log.action
                              ?.replace(/_/g, " ")
                              .toLowerCase()
                              .replace(/\b\w/g, (char) => char.toUpperCase())}
                          </Text>
                        </Flex>

                        <Text fontSize="11px" color="#9CA3AF" ml="auto" className="font-poppins text_regular text_sm">
                          {/* {date} */}
                          {showDateTime(log.createdAt)}
                        </Text>
                      </Flex>

                      <LogCard log={log} />
                    </Box>
                  </Flex>
                );
              })}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
};

export default UniversalActivityHistory;